import { ResponseResolver, restContext, RestRequest } from 'msw';
import { delayedResponse } from '@/utils/useMswCustomResponse';
import { choiceRandom } from '@/utils/useHelper';
import dayjs from 'dayjs';
import {
  MonthlyPayment,
  MonthlyPaymentsResponse,
  MonthlyPaymentUsage,
  PaymentConfirmResponse,
  PaymentGetResultResponse,
} from '../types/payment';
import { MONTHLY_PAYMENT_USAGE } from '../../constants/payment';

const putPaymentMethod: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  const body = await req.json();

  if (!body['cardId']) {
    return delayedResponse(
      ctx.status(404),
      ctx.json({
        messages: ['カードIDが必要です'],
        code: 100,
      }),
    );
  }
  return delayedResponse(ctx.status(204));
};

const postPaymentConfirm: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const body = await req.json();
  if (
    !body['applicationId'] ||
    !body['usage'] ||
    !body['totalAmount'] ||
    !body['paymentType']
  ) {
    return delayedResponse(
      ctx.status(404),
      ctx.json({
        messages: ['必須項目の入力値が不足しています'],
        code: 100,
      }),
    );
  }
  return delayedResponse(
    ctx.status(200),
    ctx.json<PaymentConfirmResponse>({
      addYumenaviPoint: 100,
      paymentAmountWithDiscount: 50000,
      useYumenaviPoint: 1000,
    }),
  );
};

const postPayment: ResponseResolver<RestRequest, typeof restContext> = async (
  req,
  res,
  ctx,
) => {
  const body = await req.json();
  if (
    !body['applicationId'] ||
    !body['usage'] ||
    !body['totalAmount'] ||
    !body['paymentType'] ||
    (body['paymentType'] === 1 && !body['cardId'])
  ) {
    return delayedResponse(
      ctx.status(404),
      ctx.json({
        messages: ['不正な入力値または項目が不足'],
        code: 100,
      }),
    );
  }
  if (body['paymentType'] === 1) {
    return delayedResponse(ctx.status(200), ctx.body('https://example.com/'));
  } else {
    const successRedirectionQuery = decodeURIComponent(
      body['successRedirectionQuery'],
    );
    const errorRedirectionQuery = decodeURIComponent(
      body['errorRedirectionQuery'],
    );
    const successUrl = new URL(
      successRedirectionQuery.replace('redirect_path=', ''),
      'http://localhost:3333',
    );
    const errorUrl = new URL(
      errorRedirectionQuery.replace('redirect_path=', ''),
      'http://localhost:3333',
    );
    successUrl.searchParams.set('paymentId', '1');
    errorUrl.searchParams.set('paymentId', '1');
    return delayedResponse(
      ctx.status(200),
      ctx.body(choiceRandom([successUrl.href, errorUrl.href])),
    );
  }
};

/**
 * 決済結果を取得
 */
const getPaymentResult: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const paymentId = Number(req.params.paymentId);
  if (!paymentId) {
    return delayedResponse(ctx.status(404));
  }
  return delayedResponse(
    ctx.status(200),
    ctx.json<PaymentGetResultResponse>({
      isCompleted: choiceRandom([true, false, false]),
      isSuccess: choiceRandom([true, false]),
      isCancelled: choiceRandom([true, false, false]),
    }),
  );
};

const getMonthlyPayments: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  if (!applicationId) {
    return delayedResponse(ctx.status(404));
  }
  const page = Number(req.url.searchParams.get('page')) || 1;
  const limit = Number(req.url.searchParams.get('per_page')) || 10;
  const totalItems = 50;
  const totalPages = Math.ceil(totalItems / limit);

  const payments: MonthlyPayment[] = Array.from({ length: limit }, (_, i) => ({
    id: i + 1 + (page - 1) * limit,
    applicationId,
    displayName: '初期費用',
    parkingId: 100 + i,
    parkingName: `テスト駐車場 ${i + 1}`,
    usage: choiceRandom<MonthlyPaymentUsage>([
      MONTHLY_PAYMENT_USAGE.MONTHLY_APPLICATION_INITIAL_PAYMENT,
      MONTHLY_PAYMENT_USAGE.MONTHLY_APPLICATION_RENEWAL_PAYMENT,
      MONTHLY_PAYMENT_USAGE.MONTHLY_BILLING_PAYMENT,
    ]),
    parkingAddress: '東京都渋谷区道玄坂1-2-3',
    isParkingDeleted: choiceRandom([true, false]),
    isReceiptExpired: choiceRandom([true, false]),
    paidAt: dayjs().subtract(i, 'day').toISOString(),
    usedAt: dayjs().subtract(i, 'day').toISOString(),
  }));

  return delayedResponse(
    ctx.status(200),
    ctx.json<MonthlyPaymentsResponse>({
      payments,
      pagination: {
        total: totalItems,
        perPage: limit,
        currentPage: page,
        lastPage: totalPages,
        firstPageUrl: `http://localhost:3333/monthly/payments?page=1&per_page=${limit}`,
        lastPageUrl: `http://localhost:3333/monthly/payments?page=${totalPages}&per_page=${limit}`,
        nextPageUrl: `http://localhost:3333/monthly/payments?page=${page + 1}&per_page=${limit}`,
        prevPageUrl: `http://localhost:3333/monthly/payments?page=${page - 1}&per_page=${limit}`,
      },
    }),
  );
};

const mockMonthlyPayment = {
  putPaymentMethod,
  postPaymentConfirm,
  postPayment,
  getPaymentResult,
  getMonthlyPayments,
};

export default mockMonthlyPayment;
